
import { uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { stringStep } from '../../../concepts/commands/CommandWizard';
import { ObjectManager } from '../../../concepts/objects';
import { resolveSubject, resolveSubjects } from '../../canvas/commands/CommandUtil';
import { RelativeTo } from '../../canvas/entities/Physical';
import { EffectDirector, Effects } from '../../canvas/visualization/EffectDirector';
import { WidgetManager } from '../../canvas/workspace/widgets/WidgetManager';
import { PopupManager } from '../../popups/PopupManager';
import { manageTagsPopup } from './ManageTagsPopup';
import { Tag } from './Tag';
import { TagSupport } from './TagSupport';
import { TagWidget } from './TagWidget';

export const tagsAdd = defineCommand(
    'tagsAdd',
    'Tags: Add',
    (r, [name, value, symbol, color, targetIds, flash]: [string, string?, string?, string?, string[]?, boolean?]) => {

        const effects = r.call(EffectDirector);
        const objects = r.call(ObjectManager);
        const subjects = resolveSubjects(r, targetIds, TagSupport);
        if (!subjects.length) return;

        return objects.update(u => {

            const result = [] as string[];

            for (const s of subjects) {
                // Skip if already tagged
                if (!!s.polarize(Tag).find(x => x.name === name)) continue;

                const tag = u.create(Tag);
                u.rolling(`edit:${tag.id}`);

                if (name) tag.name = name;
                if (value) tag.value = value;
                if (symbol) tag.symbol = symbol;
                tag.color = color || Tag.pickColor(name);
                s.add(tag);
                result.push(tag.id);

                if (flash) {
                    const rect = s.layout.tags[tag.id];
                    effects.trigger(Effects.flash(rect, tag.color), s);
                }
            }


            return result;
        });
    },
    [
        stringStep('name', 'Name'),
        stringStep('value', 'Value', ''),
        stringStep('symbol', 'Symbol', ''),
        stringStep('color', 'Color', ''),
    ]
);

export const tagsAddNew = defineCommand(
    'tagsAddNew',
    'Tags: Add New',
    (cc, [targetId]: [string?]) => {

        const objects = cc.call(ObjectManager);
        const subject = resolveSubject(cc, targetId, TagSupport);
        if (!subject) return;

        const tagId = objects.update(u => {
            const tag = u.create(Tag);
            u.rolling(`edit:${tag.id}`);
            tag.color = Tag.pickColor(uuid());
            subject.add(tag);
            return tag.id;
        });

        cc.dispatch(tagsToggleEdit, [tagId, true]);
    }
);

export const tagsDelete = defineCommand(
    'tagsDelete',
    'Tags: Delete',
    (r, [name, targetIds]: [string, string[]?]) => {

        const objects = r.call(ObjectManager);
        const subjects = resolveSubjects(r, targetIds, TagSupport);
        if (!subjects.length) return;

        objects.update(u => {

            for (const s of subjects) {
                // Find the tag
                const t = s.polarize(Tag).find(x => x.name === name);
                if (!t) continue;
                u.delete(t.removeSelf());
            }
        });
    },
    [
        stringStep('name', 'Name'),
    ]
);

export const tagsClear = defineCommand(
    'tagsClear',
    'Tags: Clear',
    (r, [targetIds]: [string[]?]) => {

        const objects = r.call(ObjectManager);
        const subjects = resolveSubjects(r, targetIds, TagSupport);
        if (!subjects.length) return;

        objects.update(u => {

            for (const s of subjects) {
                for (const t of s.polarize(Tag)) {
                    if (!t) continue;
                    u.delete(t.removeSelf());
                }
            }
        });
    },
);

// export const tagsSetColor = defineCommand(
//     'tagsSetColor',
//     'Tags: Set Color',
//     (r, [tagName, newColor]: [string, string]) => {
//         newColor = Colors.normalize(newColor);
//         if (!tagName) return;
//         if (!newColor) return;

//         const objects = r.call(ObjectManager);
//         const tags = objects.findAll(Tag, x => x.name === tagName);
//         if (!tags.length) return;

//         objects.update(() => {
//             for (const t of tags) {
//                 t.color = newColor;
//             }
//         });
//     },
//     [
//         stringStep('tagName', 'Tag Name:'),
//         stringStep('newColor', 'New Color:'),
//     ]
// );

// export const tagsSetSymbol = defineCommand(
//     'tagsSetSymbol',
//     'Tags: Set Symbol',
//     (r, [tagName, newSymbol]: [string, string]) => {
//         if (!tagName) return;
//         if (!newSymbol) return;

//         const objects = r.call(ObjectManager);
//         const tags = objects.findAll(Tag, x => x.name === tagName);
//         if (!tags.length) return;

//         objects.update(() => {
//             for (const t of tags) {
//                 t.symbol = newSymbol;
//             }
//         });
//     },
//     [
//         stringStep('tagName', 'Tag Name:'),
//         stringStep('newSymbol', 'New Symbol:'),
//     ]
// );

export const tagsToggleEdit = defineCommand(
    'tagsToggleEdit',
    null,
    (r, [targetId, open]: [string?, boolean?]) => {
        open = !!open;

        const widgets = r.call(WidgetManager);
        widgets.delete('tags');

        if (!open || !targetId) return;

        const objects = r.call(ObjectManager);
        const subject = objects.getMaybe(targetId, Tag);
        if (!subject) return;

        const parent = subject.parent?.as(TagSupport);
        if (!parent) return;

        const at = parent.layout.tags[subject.id].offset(parent.position(RelativeTo.Model))
            .pointAt(.5, 0);

        const cont = widgets.createContainer({
            name: 'tags',
            position: ['absolute', { at, relativeTo: 'model', zOrder: 110 }],
            direction: 'row',
            style: 'none',
        });

        cont.add(widgets.createInline({ ui: TagWidget, subject }));
        widgets.add(cont);
        return true;
    }
);

export const tagsManage = defineCommand(
    'tagsManage',
    'Tags: Manage',
    r => {
        const popups = r.call(PopupManager);
        popups.invoke(manageTagsPopup());
    }
);

export const inTaggingCommands = () => inCommands(
    tagsAdd, tagsDelete, tagsClear,
);
